import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { AtokuService } from '../services/atoku.service';
import { AtokuStatusPillConfig, getBlockedStatusPill, getSubscriptionStatusPill } from '../common/atoku-status-pill.util';

interface MockAtokuAccessUser {
    id: string | number;
    name: string;
    email: string;
    subscriptionState: 'subscribed' | 'suspended' | 'NaN';
    blocked: boolean | null;
    lastEntryAt: string;
    entriesToday: number | 'NaN';
}

interface AccessEntryRow {
    localDateTime: string;
    eventType: string;
    doorName: string;
    status: string;
}

@Component({
    selector: 'app-atoku-access-management',
    templateUrl: './atoku-access-management.component.html',
    styleUrls: ['./atoku-access-management.component.scss']
})
export class AtokuAccessManagementComponent implements AfterViewInit {
    displayedColumns: string[] = ['name', 'email', 'subscriptionState', 'blocked', 'entriesToday', 'lastEntryAt', 'actions'];
    entryColumns: string[] = ['localDateTime', 'eventType', 'doorName', 'status'];

    users: MockAtokuAccessUser[] = [];
    dataSource = new MatTableDataSource<MockAtokuAccessUser>(this.users);
    selectedUser: MockAtokuAccessUser | null = null;

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(
        private readonly snackBar: MatSnackBar,
        private readonly atokuService: AtokuService
    ) {
        this.dataSource.filterPredicate = (row, filter) =>
            [row.name, row.email, row.subscriptionState, row.blocked ? 'blocked' : 'active']
                .join(' ')
                .toLowerCase()
                .includes(filter);
        this.loadUsers();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
    }

    applyFilter(event: Event): void {
        const value = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.dataSource.filter = value;
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    selectUser(user: MockAtokuAccessUser): void {
        this.selectedUser = user;
    }

    toggleSubscription(user: MockAtokuAccessUser): void {
        if (user.subscriptionState === 'NaN') {
            return;
        }
        const nextState = user.subscriptionState === 'subscribed' ? 'suspended' : 'subscribed';
        const call = nextState === 'subscribed'
            ? this.atokuService.subscribeConsoleUser({ email: user.email })
            : this.atokuService.unsubscribeConsoleUser({ email: user.email });
        call.subscribe(() => {
            this.updateUser(user.id, { subscriptionState: nextState });
            this.snackBar.open(`${user.name} ${nextState === 'subscribed' ? 'subscribed' : 'unsubscribed'}`, 'Close', { duration: 2500 });
        });
    }

    subscribeUser(user: MockAtokuAccessUser): void {
        this.atokuService.subscribeConsoleUser({ email: user.email })
            .subscribe(() => {
                this.updateUser(user.id, { subscriptionState: 'subscribed' });
                this.snackBar.open(`${user.name} subscribed`, 'Close', { duration: 2500 });
            });
    }

    unsubscribeUser(user: MockAtokuAccessUser): void {
        this.atokuService.unsubscribeConsoleUser({ email: user.email })
            .subscribe(() => {
                this.updateUser(user.id, { subscriptionState: 'suspended' });
                this.snackBar.open(`${user.name} unsubscribed`, 'Close', { duration: 2500 });
            });
    }

    blockUser(user: MockAtokuAccessUser): void {
        if (user.blocked === null) {
            return;
        }
        this.atokuService.blockConsoleUser({ email: user.email, blocked: true })
            .subscribe(() => {
                this.updateUser(user.id, { blocked: true });
                this.snackBar.open(`${user.name} blocked`, 'Close', { duration: 2500 });
            });
    }

    unblockUser(user: MockAtokuAccessUser): void {
        if (user.blocked === null) {
            return;
        }
        this.atokuService.blockConsoleUser({ email: user.email, blocked: false })
            .subscribe(() => {
                this.updateUser(user.id, { blocked: false });
                this.snackBar.open(`${user.name} unblocked`, 'Close', { duration: 2500 });
            });
    }

    getSelectedUserEntries(): AccessEntryRow[] {
        return [];
    }

    private updateUser(userId: string | number, patch: Partial<MockAtokuAccessUser>): void {
        this.users = this.users.map(user => user.id === userId ? { ...user, ...patch } : user);
        this.dataSource.data = this.users;
        if (this.selectedUser?.id === userId) {
            this.selectedUser = this.users.find(user => user.id === userId) ?? null;
        }
    }

    private loadUsers(): void {
        this.atokuService.getConsoleUsers().subscribe(response => {
            this.users = (response.items ?? []).map(item => {
                const raw = item as Record<string, unknown>;
                const subscriptionState = this.normalizeText(raw['subscription_state'] ?? raw['subscriptionState']);
                return {
                    id: this.normalizeText(raw['user_id'] ?? raw['site_user_id']),
                    name: `${this.normalizeText(raw['first_name'] ?? raw['firstName'])} ${this.normalizeText(raw['last_name'] ?? raw['lastName'])}`.trim(),
                    email: this.normalizeText(raw['email']),
                    subscriptionState: subscriptionState === 'subscribed' || subscriptionState === 'suspended' ? subscriptionState : 'NaN',
                    blocked: this.toNullableBoolean(raw['blocked']),
                    lastEntryAt: this.normalizeText(raw['lastEntryAt'] ?? raw['lastBooking'] ?? raw['data_removal_expires_at']),
                    entriesToday: this.toNullableNumber(raw['entriesToday'])
                };
            });
            this.dataSource.data = this.users;
            this.selectedUser = this.users[0] ?? null;
        });
    }

    getBlockedLabel(user: MockAtokuAccessUser): string {
        if (user.blocked === null) {
            return 'NaN';
        }
        return user.blocked ? 'Yes' : 'No';
    }

    getSubscriptionPill(user: MockAtokuAccessUser): AtokuStatusPillConfig {
        return getSubscriptionStatusPill(user.subscriptionState);
    }

    getBlockedPill(user: MockAtokuAccessUser): AtokuStatusPillConfig {
        return getBlockedStatusPill(user.blocked);
    }

    getLastEntryLabel(user: MockAtokuAccessUser): string {
        if (user.lastEntryAt === 'NaN') {
            return 'NaN';
        }
        const parsed = new Date(user.lastEntryAt);
        return Number.isNaN(parsed.getTime()) ? 'NaN' : parsed.toLocaleString();
    }

    private normalizeText(value: unknown): string {
        const text = String(value ?? '').trim();
        if (!text || text.toLowerCase() === 'not_available') {
            return 'NaN';
        }
        return text;
    }

    private toNullableBoolean(value: unknown): boolean | null {
        if (typeof value === 'boolean') {
            return value;
        }
        const text = String(value ?? '').trim().toLowerCase();
        if (!text || text === 'not_available') {
            return null;
        }
        if (text === 'true') {
            return true;
        }
        if (text === 'false') {
            return false;
        }
        return null;
    }

    private toNullableNumber(value: unknown): number | 'NaN' {
        const text = String(value ?? '').trim().toLowerCase();
        if (!text || text === 'not_available') {
            return 'NaN';
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 'NaN';
    }
}
